import { forwardRef, useMemo, useRef } from 'react';
import classNames from 'classnames';
import FlexContainer from '../FlexContainer';
import { checkAcceptWithMime } from './mimeHepers';
import styles from './InputFile.module.scss';

/**
 * InputFile
 *
 * @return {JSX.Element}
 */
const InputFile = (
  { className, children, onChange, useDrag, accept, flex, keepValue, ...rest },
  ref
) => {
  const inputRefLocal = useRef(null);
  const inputRef = ref || inputRefLocal;
  const containerRef = useRef(null);

  const handleChange = (e) => {
    onChange(e);
    if (!keepValue) {
      inputRef.current.value = null;
    }
  };

  const handlePreventDefault = useMemo(
    () =>
      useDrag
        ? (e) => {
            e.preventDefault();
            return false;
          }
        : undefined,
    [useDrag]
  );

  const handleDrop = useMemo(
    () =>
      useDrag
        ? (e) => {
            if (e.dataTransfer.files.length) {
              const dT = new DataTransfer();
              for (let file of e.dataTransfer.files) {
                const isValid = checkAcceptWithMime(
                  accept,
                  file.type,
                  file.name
                );
                if (isValid) {
                  dT.items.add(file);
                }
              }

              if (dT.items.length) {
                inputRef.current.files = dT.files;

                onChange({ ...e, target: inputRef.current });
              }
            }
            e.target.classList.remove(styles.DragOver);
            e.preventDefault();
          }
        : undefined,
    [useDrag, accept, onChange, inputRef]
  );

  const handleDragEnter = useMemo(
    () =>
      useDrag
        ? (e) => {
            e.currentTarget.classList.add(styles.DragOver);
            e.preventDefault();
          }
        : undefined,
    [useDrag]
  );

  const handleDragExit = useMemo(
    () =>
      useDrag
        ? (e) => {
            if (e.target === e.currentTarget) {
              e.currentTarget.classList.remove(styles.DragOver);
            }
            e.preventDefault();
          }
        : undefined,
    [useDrag]
  );

  return (
    <FlexContainer
      ref={containerRef}
      className={classNames(
        styles.InputFile,
        className,
        `${InputFile.displayName}`
      )}
      onDragEnter={handleDragEnter}
      onDragOver={handlePreventDefault}
      onDragLeave={handleDragExit}
      onDrop={handleDrop}
      flex={flex}
    >
      <label className={styles.Label}>
        <input
          className={styles.Input}
          type="file"
          {...rest}
          accept={accept}
          ref={inputRef}
          onChange={handleChange}
        />
        <FlexContainer className={styles.Holder} flex>
          {children}
        </FlexContainer>
      </label>
    </FlexContainer>
  );
};

InputFile.displayName = 'InputFile';

export default forwardRef(InputFile);
